// scheduler-red-window-stall-explainer.ts
//
// Phase 06j (F-48, R2): why the RED-window rule stalled a `run`, rendered from
// the SAME `held[]` the scheduler computed (one source ⇒ a stall it caused is
// never explained by an empty list). One line per cause; every held ticket is
// named in exactly one line, after "giữ". Each fix is the only shape `redirect`
// has — `avatar autopilot redirect --mission <id> --text "…"` (no amend/create
// sub-commands; the text becomes a tier-1 decision ticket whose proposals are
// applied in array order). Cancel alone never unblocks (a cancelled dep does
// not satisfy `depends_on` and the applier blocks direct dependents) ⇒ cancel is
// only suggested AFTER an amend that drops the edge, in the same proposal.
import { isReady, priorityTier, type TicketRuntimeStatus } from "./scheduler.js";
import {
  buildRedWindowGraph,
  filterRedWindowCandidates,
  type HeldTicket,
  isRetiredStatus,
  type RedWindowGraph,
} from "./scheduler-red-window-rule.js";
import type { Ticket } from "./schemas/ticket-schema.js";

const SUFFIX =
  "(--text tạo ticket decision — tầng 1, cửa sổ RED không giữ — ở run kế; proposal amend đổi plan ⇒ G2 sẽ hỏi lại, approve g2 rồi ticket bị giữ mới chạy)";

const list = (ids: Iterable<string>) => [...ids].join(", ");

// Review 06j L1: `depends_on` is z.array(z.string()) — only ids shaped like a
// ticket id go inside the `--text "…"` hint (no quote/shell char can close it).
const isTicketId = (id: string) => /^T-\d{4,}$/.test(id);

function redirect(missionId: string | undefined, text: string): string {
  const mission = missionId === undefined ? "" : ` --mission ${missionId}`;
  return `avatar autopilot redirect${mission} --text "${text}"`;
}

function liveDependents(g: RedWindowGraph, id: string): string[] {
  return (g.dependents.get(id) ?? [])
    .map((t) => t.id)
    .filter((d) => g.status(d) !== "done" && !isRetiredStatus(g.status(d)))
    .sort();
}

/** One open window: what it waits on + the rendered redirect commands that unstick it. */
function describeWindow(
  g: RedWindowGraph,
  x: string,
  missionId: string | undefined,
): { text: string; fixes: string[] } {
  const ys = g.green(x);
  const pending = ys.filter((y) => g.status(y) !== "done" && !isRetiredStatus(g.status(y)));
  if (pending.length === 0) {
    const rewire = ys.flatMap((y) =>
      liveDependents(g, y).map(
        (d) => `amend ${d}: depends_on thay ${y} bằng ticket mới, đổi acceptance theo ticket mới`,
      ),
    );
    const create = `create implement mới (hoặc fix gỡ test đỏ) depends_on ${x}`;
    return {
      text: `testcase ${x} đã done, mọi implement của ${x} đã cancel/supersede`,
      fixes: [redirect(missionId, [create, ...rewire].join("; "))],
    };
  }
  const parts: string[] = [];
  const fixes: string[] = [];
  for (const y of pending) {
    const status = g.status(y);
    parts.push(`test RED chờ ${y} (${status})`);
    if (status !== "todo") {
      const rewire = liveDependents(g, y).map(
        (d) => `, amend ${d}: depends_on thay ${y} bằng ticket mới`,
      );
      const fix = `xử lý ${y} (${status}): amend ${y} gỡ lý do ${status} và đổi acceptance; hoặc create implement mới depends_on ${x}, supersede ${y}${rewire.join("")}`;
      fixes.push(redirect(missionId, fix));
      continue;
    }
    const blockers = (g.byId.get(y)?.depends_on ?? []).filter(
      (d) => isTicketId(d) && g.status(d) !== "done",
    );
    const outside = list(blockers.filter((d) => !g.cluster(x).has(d)));
    if (outside === "") {
      if (blockers.length > 0) parts.push(`${y} chờ ${list(blockers)} (xem dòng trên)`);
      continue;
    }
    const drop = redirect(missionId, `amend ${y}: bỏ ${outside} khỏi depends_on`);
    const merge = redirect(
      missionId,
      `amend ${y}: gộp phạm vi ${outside} vào ${y} (allowed_paths, acceptance) và bỏ ${outside} khỏi depends_on; sau đó cancel ${outside}; amend đứng trước cancel trong cùng proposed_changes`,
    );
    fixes.push(
      `${drop} — ${outside} chạy sau khi suite xanh. Nếu ${y} thật cần thay đổi của ${outside} (vd gỡ injection README): ${merge}`,
    );
  }
  return { text: `testcase ${x} đã done, ${parts.join(", ")}`, fixes };
}

function windowLine(g: RedWindowGraph, held: string[], missionId?: string): string {
  const windows = g.openWindows().map((x) => describeWindow(g, x, missionId));
  const fixes = windows.flatMap((w) => w.fixes);
  const fixPart = fixes.length > 0 ? ` Gỡ: ${fixes.join("; hoặc ")} ${SUFFIX}` : "";
  return `red_window_conflict: ${windows.map((w) => w.text).join("; ")}; giữ ${list(held)} (ngoài cửa sổ — chạy lúc này verify fail vì suite đỏ).${fixPart}`;
}

function disjointLine(g: RedWindowGraph, held: string[], missionId?: string): string {
  const windows = g.openWindows();
  const pendingOf = (x: string) =>
    g.green(x).filter((y) => g.status(y) !== "done" && !isRetiredStatus(g.status(y)));
  const desc = windows.map((x) => `${x} (chờ ${list(pendingOf(x)) || "implement mới"})`);
  const [w1, ...others] = windows;
  const y1 = pendingOf(w1 ?? "")[0];
  const fix =
    y1 === undefined
      ? `create implement mới depends_on ${list(windows)}, làm xanh test của cả ${list(windows)}`
      : `amend ${y1}: thêm ${list(others)} vào depends_on, mở allowed_paths và acceptance để làm xanh cả test của ${list(others)}`;
  return `red_window_conflict: disjoint_windows — ${windows.length} cửa sổ RED rời nhau: ${list(desc)}; giữ ${list(held)} — implement nào chạy lúc này cũng fail verify vì test đỏ của cửa sổ kia. Gỡ: ${redirect(missionId, fix)} ${SUFFIX}`;
}

function prerequisiteLine(g: RedWindowGraph, held: string[], missionId?: string): string {
  const pairs = held.flatMap((x) => g.prerequisites(x).map((p) => ({ x, ...p })));
  const waits = [
    ...new Set(
      pairs.map(
        (p) =>
          `${g.byId.get(p.green)?.kind} ${p.green} còn chờ ${p.waitsOn} (${g.status(p.waitsOn)})`,
      ),
    ),
  ];
  const fixes = new Map<string, Set<string>>();
  for (const p of pairs) {
    const drop = (g.byId.get(p.green)?.depends_on ?? []).filter(
      (d) =>
        isTicketId(d) &&
        (d === p.waitsOn || g.ancestors(d).has(p.waitsOn)) &&
        !g.cluster(p.x).has(d) &&
        d !== p.x,
    );
    for (const d of drop) fixes.set(p.green, (fixes.get(p.green) ?? new Set()).add(d));
  }
  const cmds = [...fixes].map(([y, ds]) =>
    redirect(missionId, `amend ${y}: bỏ ${list(ds)} khỏi depends_on`),
  );
  const ns = list(new Set(pairs.map((p) => p.waitsOn)));
  // No direct edge to drop (N reaches Y only through another cluster ticket) ⇒ no command.
  const fixPart = cmds.length > 0 ? ` Gỡ: ${cmds.join(" hoặc ")} ${SUFFIX}` : "";
  return `red_window_conflict: giữ testcase ${list(held)} — ${waits.join(", ")}; ${ns} phải chạy trước khi ${list(held)} làm suite đỏ.${fixPart}`;
}

/** `[]` unless the RED-window rule is what leaves nothing to dispatch (busy tick, tier 0/1 ready, or candidates left ⇒ no conflict). */
export function explainRedWindowConflict(
  tickets: Ticket[],
  statusMap: Record<string, TicketRuntimeStatus>,
  missionId?: string,
): string[] {
  if (tickets.some((t) => (statusMap[t.id] ?? "todo") === "in_progress")) return [];
  const ready = tickets.filter((t) => isReady(t, statusMap));
  if (ready.length === 0 || ready.some((t) => priorityTier(t.kind) < 2)) return [];
  const { candidates, held } = filterRedWindowCandidates(ready, tickets, statusMap);
  if (candidates.length > 0 || held.length === 0) return [];
  const graph = buildRedWindowGraph(tickets, statusMap);
  const ids = (cause: HeldTicket["cause"]) =>
    held
      .filter((h) => h.cause === cause)
      .map((h) => h.id)
      .sort();
  const lines: string[] = [];
  if (ids("disjoint_windows").length > 0)
    lines.push(disjointLine(graph, ids("disjoint_windows"), missionId));
  if (ids("prerequisite").length > 0)
    lines.push(prerequisiteLine(graph, ids("prerequisite"), missionId));
  if (ids("window").length > 0) lines.push(windowLine(graph, ids("window"), missionId));
  return lines;
}
